/**
 * @file index.tsx
 *
 * @fileoverview Shows a card with organization teaser.
 */
import { cva, VariantProps } from "class-variance-authority";
import React, { ReactNode } from "react";
import BadgeIcon from "@jq-icons/Badge";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import Box from "../../../02-atoms/07-box";
import styles from "./organizationTeaser.module.scss";

const teaserWrapper = cva([styles.organizationTeaserWrapper], {
  variants: {
    premium: {
      true: styles.organizationTeaserWrapperPremium,
    },
  },
});

const organizationLogoWrapper = cva([styles.organizationLogoWrapper], {
  variants: {
    hasBanner: {
      true: styles.organizationLogoWrapperHasBanner,
    },
  },
});

export type OrganizationTeaserProps = VariantProps<typeof teaserWrapper> & {
  /**
   * The title. Ca be a node (like a heading speficied in the usage context) or just a string.
   */
  name: string | React.ReactNode;
  /**
   * The banner for the organization.
   */
  banner?: React.ReactNode;
  /**
   * The logo for the organization.
   */
  logo?: React.ReactNode;
  /**
   * Fields that will be shown bellow the title.
   */
  fields?: React.ReactNode;
  /**
   * Resume actions in the form of buttons or other.
   */
  actions?: React.ReactNode;
  /**
   * The promoted organization icon for the job.
   */
  promotedOrganizationIcon?: ReactNode;
};

/**
 * The organization teaser can be used to display organizations in a list or on the homepage in the form of cards.
 */
export const OrganizationTeaser = ({
  name,
  fields,
  actions,
  banner,
  logo,
  premium = false,
  promotedOrganizationIcon,
}: OrganizationTeaserProps) => {
  const PromotedOrgIcon = promotedOrganizationIcon ?? (
    <BadgeIcon width="25" height="25" />
  );

  return (
    <Box
      className={`${teaserWrapper({ premium })} jcl-organization-teaser`.trim()}
    >
      {banner}
      {logo && (
        <div className={organizationLogoWrapper({ hasBanner: !!banner })}>
          {logo}
        </div>
      )}
      <div
        className={`
          ${styles.organizationContentWrapper}
          jcl-organization-teaser-content`.trim()}
      >
        <div
          className={`
            ${styles.organizationTeaserTitle}
            jcl-organization-teaser-title`.trim()}
        >
          <FlexItems justifyContent="space-between" gap={4}>
            <FlexItem>{name}</FlexItem>
            <FlexItem>
              {premium && (
                <div className={styles.featuredIcon}>{PromotedOrgIcon}</div>
              )}
            </FlexItem>
          </FlexItems>
        </div>
        <Spacing size="medium" />
        <div className="jcl-organization-teaser-fields">{fields}</div>
        <Spacing size="medium" />
        {actions}
      </div>
    </Box>
  );
};
export default OrganizationTeaser;
